import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { format } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { useComments, useOpportunity, useOpportunityMutations } from './useOpportunities';
import { useShareOpportunity } from './usePostShare';
import { ReportPostDialog } from './components/ReportPostDialog';
import { PostBody } from './components/PostBody';
import { CommentList } from './components/CommentList';
import { CommentInput } from './components/CommentInput';
import { avatarColor, initials } from './postVisuals';
import { stripMarkdown } from './stripMarkdown';

/**
 * Full-page view for a single post. Public — anonymous visitors can land
 * here from a shared URL (SEO + viral). Engagement buttons are display-only
 * in phase 1.
 */
export function OpportunityDetailPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  const { post, isLoading, isError } = useOpportunity(postId ?? null);
  const { user, isAuthenticated, hasRole } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { remove, toggleLike, toggleSave } = useOpportunityMutations();
  const share = useShareOpportunity();
  const commentsState = useComments(postId ?? null);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const isAuthor = Boolean(post && user && post.author.userId === user.id);
  // Admins (SUPER_ADMIN / ADMIN) can edit + delete any post at any time —
  // the backend bypasses both the ownership check and the 15-minute edit
  // window for these roles, so we surface the same options in the UI.
  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('ADMIN');
  const canModify = isAuthor || isAdmin;
  const canReport = isAuthenticated && !isAuthor && Boolean(post);

  const closeMenu = () => setMenuAnchor(null);

  const handleDelete = async () => {
    if (!post) return;
    try {
      await remove.mutateAsync(post.id);
      notify.success(t('opportunities.actions.deleted'));
      navigate('/opportunities', { replace: true });
    } catch (err) {
      notify.error(translate(err));
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  // Phase 3 engagement handlers — anonymous visitors are bounced to /login
  // with a return path so they land back on this exact post after sign-in.
  const bounceToLogin = () => {
    if (!post) return;
    notify.info(t('opportunities.actions.loginToPost'));
    navigate(
      '/login?next=' + encodeURIComponent(`/opportunities/${post.id}`),
    );
  };
  const handleLike = () => {
    if (!post) return;
    if (!isAuthenticated) return bounceToLogin();
    toggleLike.mutate(post.id, { onError: (err) => notify.error(translate(err)) });
  };
  const handleSave = () => {
    if (!post) return;
    if (!isAuthenticated) return bounceToLogin();
    toggleSave.mutate(post.id, { onError: (err) => notify.error(translate(err)) });
  };
  const handleShare = () => {
    if (!post) return;
    void share({
      postId: post.id,
      title: post.title,
      bodyPreview: stripMarkdown(post.body),
    });
  };

  useEffect(() => {
    if (!postId) navigate('/opportunities', { replace: true });
  }, [postId, navigate]);

  useEffect(() => {
    if (post?.title) {
      document.title = `${post.title} — ${t('opportunities.title')}`;
    }
  }, [post?.title, t]);

  if (!postId) return null;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 5 }, px: { xs: 0, sm: 2, md: 3 } }}>
      {/* Back button row — keeps users one tap away from the feed. */}
      <Stack direction="row" alignItems="center" sx={{ px: { xs: 1, sm: 0 }, mb: 1 }}>
        <IconButton
          onClick={() => navigate('/opportunities')}
          aria-label={t('common.back', 'Retour')}
          edge="start"
        >
          <ArrowBackRoundedIcon />
        </IconButton>
      </Stack>

      {isError ? (
        <Box sx={{ px: { xs: 2, sm: 0 } }}>
          <Alert severity="error">{t('opportunities.postNotFound')}</Alert>
        </Box>
      ) : isLoading || !post ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 4 },
            // Single column on phones, side-by-side panel on tablet+ so the
            // card reads as landscape (width > height).
            gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 38%) 1fr' },
            alignItems: 'start',
            bgcolor: 'background.paper',
            border: { xs: 'none', sm: '1px solid' },
            borderColor: 'divider',
            borderRadius: { xs: 0, sm: 3 },
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Left panel — author, title, cover, engagement (acts as a
              "context column" on desktop; stacks first on mobile). */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              // On md+, keep the meta panel sticky-feeling near the top while
              // the long body scrolls naturally beside it.
              position: { md: 'sticky' },
              top: { md: 24 },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                src={post.author.avatarUrl ?? undefined}
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: avatarColor(post.author.userId),
                  color: 'common.white',
                  fontSize: 15,
                  fontWeight: 600,
                }}
              >
                {initials(post.author.name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 15 }} noWrap>
                  {post.author.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(new Date(post.createdAt), 'd MMM yyyy · HH:mm', {
                    locale: dateLocale,
                  })}
                </Typography>
              </Box>
              <Chip
                label={t(`opportunities.category.${post.category}`)}
                size="small"
                sx={{ fontWeight: 600, flexShrink: 0 }}
              />
              {(canModify || canReport) && (
                <IconButton
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  aria-label={t('common.actions')}
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  <MoreVertRoundedIcon />
                </IconButton>
              )}
            </Stack>

            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.3,
                fontSize: { xs: 22, md: 26 },
                lineHeight: 1.25,
              }}
            >
              {post.title}
            </Typography>

            {post.imageLargeUrl && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16 / 9',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'action.hover',
                }}
              >
                <Box
                  component="img"
                  src={post.imageLargeUrl}
                  alt=""
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            )}

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                fontSize: 14,
                flexWrap: 'wrap',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={handleLike}
                  disabled={toggleLike.isPending}
                  aria-label={post.likedByMe
                    ? t('opportunities.actions.unlike')
                    : t('opportunities.actions.like')}
                  aria-pressed={post.likedByMe}
                  sx={{ color: post.likedByMe ? 'error.main' : 'inherit' }}
                >
                  {post.likedByMe
                    ? <FavoriteRoundedIcon sx={{ fontSize: 20 }} />
                    : <FavoriteBorderRoundedIcon sx={{ fontSize: 20 }} />}
                </IconButton>
                <span>{post.likeCount}</span>
              </Stack>
              {/* Comment icon — scrolls to the thread section below. */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pl: 0.5 }}>
                <IconButton
                  size="small"
                  aria-label={t('opportunities.comments.jumpTo')}
                  onClick={() =>
                    document
                      .getElementById('comments')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                >
                  <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <span>{post.commentCount}</span>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={handleSave}
                  disabled={toggleSave.isPending}
                  aria-label={post.savedByMe
                    ? t('opportunities.actions.unsave')
                    : t('opportunities.actions.save')}
                  aria-pressed={post.savedByMe}
                  sx={{ color: post.savedByMe ? 'primary.main' : 'inherit' }}
                >
                  {post.savedByMe
                    ? <BookmarkRoundedIcon sx={{ fontSize: 20 }} />
                    : <BookmarkBorderRoundedIcon sx={{ fontSize: 20 }} />}
                </IconButton>
                <span>{post.saveCount}</span>
              </Stack>
              <Box sx={{ ml: 'auto' }}>
                <IconButton
                  size="small"
                  onClick={handleShare}
                  aria-label={t('opportunities.actions.share')}
                >
                  <ShareOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Stack>
          </Box>

          {/* Right panel — body content + comments thread. On md+, a thin
              divider separates it from the context column. Markdown is
              rendered via {@code PostBody}; the raw markdown source lives
              in the DB and is shipped to the client as-is. */}
          <Box
            sx={{
              borderLeft: { xs: 'none', md: '1px solid' },
              borderColor: { md: 'divider' },
              pl: { xs: 0, md: 4 },
            }}
          >
            {/* Long posts scroll inside the card instead of pushing the
                rest of the page down — keeps the meta panel + comments
                section visible without a tall page-level scrollbar. */}
            <Box
              sx={{
                maxHeight: { xs: '55vh', md: '65vh' },
                overflowY: 'auto',
                pr: { xs: 0, md: 1 },
              }}
            >
              <PostBody source={post.body} />
            </Box>

            {/* Comments section — the outer Box scrolls on its own (so the
                post body above stays put while the user explores the thread),
                and the composer at the top is sticky so the user can reply
                without scrolling back up. The #comments anchor lets external
                links jump straight to this region. */}
            <Box
              id="comments"
              sx={{
                mt: { xs: 3, md: 4 },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                // Internal scroll: long threads don't push the rest of the
                // page off-screen. Capped at ~viewport-height so the section
                // never dwarfs the rest of the post on tall monitors.
                maxHeight: { xs: '60vh', md: '70vh' },
                overflowY: 'auto',
                // The sticky header below relies on this container being the
                // scroll context — `position: sticky` anchors to the nearest
                // scrolling ancestor.
                position: 'relative',
              }}
            >
              {/* Sticky composer block — title + input stay pinned to the
                  top of the scrolling region while comments flow underneath. */}
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  p: { xs: 2, md: 3 },
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 700, mb: 2 }}
                >
                  {commentsState.total > 0
                    ? t('opportunities.comments.titleWithCount', {
                        count: commentsState.total,
                      })
                    : t('opportunities.comments.title')}
                </Typography>
                <CommentInput postId={post.id} onAnonymous={bounceToLogin} />
              </Box>

              {/* Scrolling thread below the sticky composer. */}
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <CommentList
                  postId={post.id}
                  comments={commentsState.comments}
                  isLoading={commentsState.isLoading}
                  isError={commentsState.isError}
                  hasMore={commentsState.hasMore}
                  isFetchingMore={commentsState.isFetchingMore}
                  onLoadMore={commentsState.loadMore}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Author + report menu — rendered at the page level so the anchor
          works even when the post column re-flows on resize. */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {canModify && post && (
          <MenuItem
            onClick={() => {
              closeMenu();
              navigate(`/opportunities/${post.id}/edit`);
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('opportunities.actions.edit')}</ListItemText>
          </MenuItem>
        )}
        {canModify && (
          <MenuItem
            onClick={() => {
              closeMenu();
              setConfirmDeleteOpen(true);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('opportunities.actions.delete')}</ListItemText>
          </MenuItem>
        )}
        {canReport && (
          <MenuItem
            onClick={() => {
              closeMenu();
              setReportOpen(true);
            }}
          >
            <ListItemIcon>
              <FlagOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('opportunities.report.menuItem')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ReportPostDialog
        open={reportOpen}
        postId={post?.id ?? null}
        onClose={() => setReportOpen(false)}
      />

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !remove.isPending && setConfirmDeleteOpen(false)}
      >
        <DialogTitle>{t('opportunities.actions.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('opportunities.actions.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={remove.isPending}>
            {t('opportunities.form.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={remove.isPending}
            startIcon={
              remove.isPending ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {t('opportunities.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
